import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function AgentLogin() {
  const navigate = useNavigate();
  const { login, agent } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (agent) navigate("/agent");
  }, [agent, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/agent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone grid grid-cols-1 md:grid-cols-2">
      {/* Left visual */}
      <div className="hidden md:block relative bg-forest">
        <img
          src="https://images.pexels.com/photos/28529675/pexels-photo-28529675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-bone">
          <Link to="/" className="flex items-center gap-2" data-testid="back-to-home-from-login">
            <div className="w-8 h-8 bg-bone rounded-sm flex items-center justify-center">
              <Shield className="w-4 h-4 text-forest" strokeWidth={2} />
            </div>
            <span className="font-heading font-medium text-lg">InsureWell</span>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-3">Agent Portal</div>
            <h1 className="font-heading text-4xl lg:text-5xl tracking-tighter leading-[1.05] font-medium max-w-md">
              The control room for every application.
            </h1>
            <p className="text-bone/70 mt-5 max-w-sm leading-relaxed">
              Triage submissions, review medical & financial details, and manage client status from a single dense workspace.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden text-sm text-sage flex items-center gap-1 mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-3">Sign in</div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-2">Agent login</h2>
          <p className="text-sage mb-8">Use your assigned agent credentials.</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="agent-login-form">
            <div>
              <Label htmlFor="email" className="text-forest">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 h-11 border-line input-clean"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-forest">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 h-11 border-line input-clean"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-forest hover:bg-forestdark text-bone rounded-sm h-11"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
